'use client'

import {createInstance, i18n, Resource} from 'i18next'
import {I18nextProvider, initReactI18next, useTranslation as useTranslationOriginal} from 'react-i18next'
import resourcesToBackend from 'i18next-resources-to-backend'
import {COOKIE_NAME, getOptions, LANGUAGE_QUERY_PARAM, languages} from './settings'
import {createContext, ReactNode, useCallback, useContext, useRef} from 'react'
import {useSearchParams} from 'next/navigation'

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 10; // 10 years

// Holds the language resolved by `?lng=` query > cookie > Accept-Language > fallback
// at I18nProvider's first init. resetLanguage() restores it.
const DefaultLanguageContext = createContext<string | null>(null);

// The cookie is the operator-set default for the device, applied via `?lng=xx` once during
// setup and read by getServerLanguage() on subsequent SSR. Visitor picks on the language page
// stay in memory only — the kiosk is shared, so a visitor's choice must not persist into the
// next session.
function persistLanguageToCookie(lang: string) {
    document.cookie = `${COOKIE_NAME}=${lang}; path=/; max-age=${COOKIE_MAX_AGE}`;
}

async function applyLanguage(instance: i18n, code: string) {
    await instance.changeLanguage(code);
    document.documentElement.lang = code;
}

export function useI18n(namespace?: string, options: { keyPrefix?: string } = {}) {
    const translation = useTranslationOriginal(namespace, options);
    const defaultLanguage = useContext(DefaultLanguageContext);

    const changeLanguage = useCallback(async (code: string) => {
        await applyLanguage(translation.i18n, code);
    }, [translation.i18n]);

    const resetLanguage = useCallback(async () => {
        if (defaultLanguage === null) return;
        await applyLanguage(translation.i18n, defaultLanguage);
    }, [translation.i18n, defaultLanguage]);

    return {
        ...translation,
        currentLanguage: translation.i18n.resolvedLanguage,
        changeLanguage,
        resetLanguage,
    };
}

export function I18nProvider({
                                 initialLanguage,
                                 initialResources,
                                 children
                             }:
                             {
                                 initialLanguage: string,
                                 initialResources: Resource,
                                 children: ReactNode
                             }) {
    const i18nRef = useRef<i18n | null>(null);
    const defaultLanguageRef = useRef<string | null>(null);
    const searchParams = useSearchParams();

    // Priority: ?lng query param > initialLanguage (which has cookie > Accept-Language > fallback)
    const lngParam = searchParams.get(LANGUAGE_QUERY_PARAM);
    const hasLngOverride = lngParam && languages.includes(lngParam);
    const effectiveLanguage = hasLngOverride ? lngParam : initialLanguage;

    if (!i18nRef.current) {
        if (typeof window !== 'undefined' && hasLngOverride) {
            // Operator setup: `?lng=xx` pins the device default for future SSR.
            persistLanguageToCookie(effectiveLanguage);
        }

        i18nRef.current = createInstance();
        i18nRef.current
            .use(initReactI18next)
            .use(resourcesToBackend((language: string, namespace: string) => import(`./locales/${language}/${namespace}.json`)))
            .init({
                ...getOptions(),
                lng: effectiveLanguage,
                resources: initialResources,
            });

        defaultLanguageRef.current = effectiveLanguage;
    }

    return (
        <DefaultLanguageContext.Provider value={defaultLanguageRef.current}>
            <I18nextProvider i18n={i18nRef.current!}>{children}</I18nextProvider>
        </DefaultLanguageContext.Provider>
    );
}
